import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Building2, CalendarCheck, MessageSquare, Moon, PhoneCall, Sun, Target } from "lucide-react";
import {
  CommandDialog, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from "@/components/ui/command";
import { MODULES, ZONES } from "@/config/modules";
import { PERSONAS } from "@/content/personas";
import { OBJECTIONS } from "@/content/objections";
import { useTheme } from "@/components/layout/ThemeProvider";
import { Kbd } from "@/components/common/bits";
import { api } from "@/lib/api";
import { useApi } from "@/hooks/useApi";

type ContentRow = { key: string; data_json: string };

// Cross-record search hits — shape mirrors worker/routes/search.ts.
type Hit = {
  kind: "account" | "call" | "booking" | "note";
  id: number | string;
  title: string;
  subtitle?: string;
  url: string;
};

export function CommandPalette() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [hits, setHits] = useState<Hit[]>([]);
  const [searching, setSearching] = useState(false);
  const navigate = useNavigate();
  const { toggle } = useTheme();

  // Prefer the agency's own personas/objections; fall back to bundled so the
  // quick-links navigate to ids that actually exist (custom-content tenants
  // used different ids, which made the bundled links dead). Only fetched
  // while the palette is open.
  const { data: personaBlocks } = useApi<{ items: ContentRow[] }>(open ? "/api/content?kind=persona" : null);
  const { data: objectionBlocks } = useApi<{ items: ContentRow[] }>(open ? "/api/content?kind=objection" : null);
  const personaLinks = (personaBlocks?.items?.length
    ? personaBlocks.items.map((r) => { const d = parseData(r.data_json); return { id: r.key, name: String(d.name ?? r.key), skill: String(d.skill ?? "") }; })
    : PERSONAS.map((p) => ({ id: p.id, name: p.name, skill: p.skill })));
  const objectionLinks = (objectionBlocks?.items?.length
    ? objectionBlocks.items.map((r) => { const d = parseData(r.data_json); return { id: r.key, trigger: String(d.trigger ?? r.key), category: String(d.category ?? "") }; })
    : OBJECTIONS.map((o) => ({ id: o.id, trigger: o.trigger, category: o.category })));

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.key === "k" && (e.metaKey || e.ctrlKey)) || e.key === "/") {
        const el = document.activeElement;
        const typing =
          el instanceof HTMLInputElement ||
          el instanceof HTMLTextAreaElement ||
          (el as HTMLElement)?.isContentEditable;
        if (e.key === "/" && typing) return;
        e.preventDefault();
        setOpen((o) => !o);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Debounced server search. Two chars minimum matches the API and keeps
  // "a", "b" from thrashing the DB. Latest-wins guard via a local token.
  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) { setHits([]); setSearching(false); return; }
    let cancelled = false;
    setSearching(true);
    const t = setTimeout(async () => {
      try {
        const r = await api.get<{ hits: Hit[] }>(`/api/search?q=${encodeURIComponent(q)}`);
        if (!cancelled) setHits(r.hits ?? []);
      } catch {
        if (!cancelled) setHits([]);
      } finally {
        if (!cancelled) setSearching(false);
      }
    }, 180);
    return () => { cancelled = true; clearTimeout(t); };
  }, [query]);

  // Reset the search box each time the palette closes so it doesn't
  // reopen showing yesterday's query.
  useEffect(() => {
    if (!open) { setQuery(""); setHits([]); }
  }, [open]);

  const go = (path: string) => {
    setOpen(false);
    navigate(path);
  };

  const groupedHits = groupHits(hits);

  return (
    <CommandDialog open={open} onOpenChange={setOpen}>
      <CommandInput
        placeholder="Search accounts, calls, notes… or jump to a module"
        value={query}
        onValueChange={setQuery}
      />
      <CommandList>
        <CommandEmpty>
          {searching ? "Searching…" : query.trim().length >= 2 ? "No matches." : "No results."}
        </CommandEmpty>

        {/* Search results — show first when the user is typing a query. */}
        {query.trim().length >= 2 && groupedHits.account.length > 0 && (
          <CommandGroup heading="Accounts">
            {groupedHits.account.map((h) => (
              <CommandItem key={`account-${h.id}`} value={`${h.title} ${h.subtitle ?? ""}`} onSelect={() => go(h.url)}>
                <Building2 className="text-muted-foreground" />
                <span>{h.title}</span>
                {h.subtitle && <span className="ml-auto truncate text-xs text-muted-foreground">{h.subtitle}</span>}
              </CommandItem>
            ))}
          </CommandGroup>
        )}
        {query.trim().length >= 2 && groupedHits.call.length > 0 && (
          <CommandGroup heading="Call notes">
            {groupedHits.call.map((h) => (
              <CommandItem key={`call-${h.id}`} value={`${h.title} ${h.subtitle ?? ""}`} onSelect={() => go(h.url)}>
                <PhoneCall className="text-muted-foreground" />
                <span className="truncate">{h.title}</span>
                {h.subtitle && <span className="ml-auto truncate text-xs text-muted-foreground">{h.subtitle}</span>}
              </CommandItem>
            ))}
          </CommandGroup>
        )}
        {query.trim().length >= 2 && groupedHits.booking.length > 0 && (
          <CommandGroup heading="Booking notes">
            {groupedHits.booking.map((h) => (
              <CommandItem key={`booking-${h.id}`} value={`${h.title} ${h.subtitle ?? ""}`} onSelect={() => go(h.url)}>
                <CalendarCheck className="text-muted-foreground" />
                <span className="truncate">{h.title}</span>
                {h.subtitle && <span className="ml-auto truncate text-xs text-muted-foreground">{h.subtitle}</span>}
              </CommandItem>
            ))}
          </CommandGroup>
        )}
        {query.trim().length >= 2 && groupedHits.note.length > 0 && (
          <CommandGroup heading="Activity & notes">
            {groupedHits.note.map((h) => (
              <CommandItem key={`note-${h.id}`} value={`${h.title} ${h.subtitle ?? ""}`} onSelect={() => go(h.url)}>
                <MessageSquare className="text-muted-foreground" />
                <span className="truncate">{h.title}</span>
                {h.subtitle && <span className="ml-auto truncate text-xs text-muted-foreground">{h.subtitle}</span>}
              </CommandItem>
            ))}
          </CommandGroup>
        )}

        {/* Fallback nav — always available when nothing is typed, or when
            no server hits are found (so users can still reach any module). */}
        {ZONES.map((z) => {
          const items = MODULES.filter((m) => m.zone === z.id);
          if (!items.length) return null;
          return (
            <CommandGroup key={z.id} heading={z.label}>
              {items.map((m) => (
                <CommandItem
                  key={m.id}
                  value={`${m.title} ${m.keywords ?? ""} ${z.label}`}
                  onSelect={() => go(`/${m.id}`)}
                >
                  <m.icon className="text-muted-foreground" />
                  <span>{m.title}</span>
                  <span className="ml-auto text-xs text-muted-foreground">{z.label}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          );
        })}
        <CommandGroup heading="Drill a persona">
          {personaLinks.map((p) => (
            <CommandItem
              key={p.id}
              value={`drill ${p.name} ${p.skill}`}
              onSelect={() => go(`/drill-range?persona=${p.id}`)}
            >
              <Target />
              <span>{p.name}</span>
              <span className="ml-auto text-xs text-muted-foreground">{p.skill}</span>
            </CommandItem>
          ))}
        </CommandGroup>
        <CommandGroup heading="Objections">
          {objectionLinks.map((o) => (
            <CommandItem
              key={o.id}
              value={`objection ${o.trigger} ${o.category}`}
              onSelect={() => go(`/objections?card=${o.id}`)}
            >
              <ArrowRight />
              <span>“{o.trigger}”</span>
              <span className="ml-auto text-xs uppercase text-muted-foreground">{o.category}</span>
            </CommandItem>
          ))}
        </CommandGroup>
        <CommandGroup heading="Actions">
          <CommandItem value="toggle theme dark light" onSelect={() => { toggle(); setOpen(false); }}>
            <Sun className="hidden dark:block" />
            <Moon className="block dark:hidden" />
            <span>Toggle theme</span>
          </CommandItem>
        </CommandGroup>
      </CommandList>
      <div className="flex items-center justify-end gap-2 border-t border-border px-3 py-2 text-[11px] text-muted-foreground">
        <Kbd>↵</Kbd> open <Kbd>↑↓</Kbd> navigate <Kbd>esc</Kbd> close
      </div>
    </CommandDialog>
  );
}

function groupHits(hits: Hit[]): Record<Hit["kind"], Hit[]> {
  const out: Record<Hit["kind"], Hit[]> = { account: [], call: [], booking: [], note: [] };
  for (const h of hits) out[h.kind].push(h);
  return out;
}

function parseData(json: string): Record<string, unknown> {
  try { return JSON.parse(json) ?? {}; } catch { return {}; }
}
